import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import LocationPicker from './LocationPicker';
import LocationSearch from '../components/LocationSearch';
import 'autocompleter/autocomplete.css';

const LocationManager = ({ initialLocationInstances = [], onLocationsChange }) => {
    const [locationInstances, setLocationInstances] = useState(initialLocationInstances);
    const [prevLocationInstances, setPrevLocationInstances] = useState(initialLocationInstances);
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [openPickerId, setOpenPickerId] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (onLocationsChange && locationInstances !== prevLocationInstances) {
            onLocationsChange(locationInstances);
        }
    }, [locationInstances, onLocationsChange]);

    const handleSelect = (location) => {
        console.debug("Selecting")
        const newLocation = {
            id: Date.now(), // Temporary ID for new locations
            location: {
                id: location.id,
                name: location.name,
                municipality: location.municipality,
                county: location.county,
                // Kept so the map can open on the locality even after its exact position
                // is cleared. Instances loaded from the API carry these; one just added
                // from the search would otherwise lose them and fall back to the country.
                latitude: location.latitude,
                longitude: location.longitude,
            },
            address: location.address,
            // Picking a place from the search is not placing a pin on it. Copying the
            // locality's own point here would record an exact position nobody set, show
            // the "exact position" mark for it, and freeze that coordinate into the
            // practice — so a later correction to the locality, V2.12.0's included, would
            // never reach it. 0,0 is this table's "no exact position", and the map falls
            // back to the locality's point, which is the same pin and stays current.
            // It has to be 0 rather than absent: the column is a primitive double and
            // PageService would unbox a null.
            latitude: 0,
            longitude: 0,
        };

        setPrevLocationInstances(locationInstances);
        setLocationInstances(prev => [...prev, newLocation]);
        setSearchInput('');
        setSearchResults([]);
    };

    const handleRemove = (locationId) => {
        console.debug("Removing")
        setPrevLocationInstances(locationInstances);
        setLocationInstances(locationInstances => locationInstances.filter(loc => loc.id !== locationId));
    };

    const handleLocationUpdate = (locationId, newLat, newLng) => {
        console.debug("Location updated")
        setPrevLocationInstances(locationInstances);
        setLocationInstances(locationInstances => locationInstances.map(l =>
            l.id === locationId
                ? { ...l, latitude: newLat, longitude: newLng }
                : l
        ));
    };

    const handleAddressChange = (locationId, newAddress) => {
        console.debug("Address changed", locationId, newAddress);
        setPrevLocationInstances(locationInstances);
        setLocationInstances(locationInstances => locationInstances.map(l =>
            l.id === locationId
                ? { ...l, address: newAddress }
                : l
        ));
    };

    const toggleLocationPicker = (locationId) => {
        setOpenPickerId(openPickerId === locationId ? null : locationId);
    };

    const clearExactPosition = (locationId) => {
        setPrevLocationInstances(locationInstances);
        setLocationInstances(locationInstances => locationInstances.map(l =>
            l.id === locationId
                ? { ...l, latitude: 0, longitude: 0 }
                : l
        ));
        setOpenPickerId(null);
    };

    const hasExactPosition = (li) => {
        return !!(li.latitude || li.longitude);
    };

    // Where to open the map for a place with no pin of its own. Localities carry their
    // own point; a municipality or county picked from the search does not, and then
    // there is nothing honest to centre on and the picker shows the whole country.
    const locationCentre = (li) => {
        const l = li.location;
        return l && Number.isFinite(l.latitude) && Number.isFinite(l.longitude)
            ? { latitude: l.latitude, longitude: l.longitude }
            : null;
    };

    // Add hidden input for JSON data
    const locationInstancesJson = JSON.stringify(locationInstances);

    return (
        <div className="lm">
            <div className="lm__section">
                <div className="lm__section-header">
                    <i className="fa-solid fa-location-dot"></i>
                    <span>Platser</span>
                    <span className="lm__section-count">{locationInstances.length}</span>
                </div>

                <div className="lm__search">
                    <LocationSearch onLocationChange={(selected) => {
                                        handleSelect(selected.location);
                                    }}
                                    onlyLocations={true}
                                    />
                    <span className="lm__search-hint">Sök och välj en ort att lägga till</span>
                </div>

                {locationInstances.length === 0 && (
                    <div className="lm__empty">
                        <i className="fa-solid fa-map-location-dot"></i>
                        <span>Inga platser tillagda</span>
                    </div>
                )}

                <div className="lm__list">
                    {locationInstances.map(li => {
                        const location = li.location;

                        return (
                        <div key={li.id} className="lm-card">
                            <div className="lm-card__header">
                                <div className="lm-card__location">
                                    <i className="fa-solid fa-location-dot"></i>
                                    <strong>{location.name}</strong>
                                    <span className="lm-card__breadcrumb">{location.municipality.name}, {location.county.name}</span>
                                </div>
                                <button
                                    type="button"
                                    className="lm-card__remove"
                                    onClick={() => handleRemove(li.id)}
                                    title="Ta bort plats"
                                >
                                    <i className="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                            <div className="lm-card__body">
                                <label className="lm-card__label"><i className="fa-solid fa-map-pin"></i> Adress</label>
                                <textarea
                                    className="form-control lm-card__address"
                                    onChange={(e) => handleAddressChange(li.id, e.target.value)}
                                    defaultValue={li.address}
                                    rows={2}
                                    placeholder="Gatuadress, postnummer, ort"
                                ></textarea>
                            </div>
                            <div className="lm-card__actions">
                                <button
                                    type="button"
                                    className={`btn btn-sm ${openPickerId === li.id ? 'btn-primary' : hasExactPosition(li) ? 'btn-success' : 'btn-outline-primary'}`}
                                    onClick={() => toggleLocationPicker(li.id)}
                                >
                                    <i className={`fa-solid ${hasExactPosition(li) && openPickerId !== li.id ? 'fa-circle-check' : 'fa-map-location-dot'}`}></i>
                                    {openPickerId === li.id ? ' Stäng karta' : ' Exakt position'}
                                </button>
                                {hasExactPosition(li) && openPickerId !== li.id && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger lm-card__clear-position"
                                        onClick={() => clearExactPosition(li.id)}
                                        title="Ta bort exakt position"
                                    >
                                        <i className="fa-solid fa-xmark"></i>
                                    </button>
                                )}
                            </div>
                            {openPickerId === li.id && (
                                <div className="lm-card__map">
                                    {/* A cleared place is stored as 0,0 because the column
                                        cannot be null. The picker wants to know there is no
                                        position, not to drop a pin in the Gulf of Guinea —
                                        but it should still open on the locality, so the
                                        click that sets a pin lands near the right town. */}
                                    <LocationPicker
                                        locationId={li.id}
                                        initialLatitude={hasExactPosition(li) ? li.latitude : null}
                                        initialLongitude={hasExactPosition(li) ? li.longitude : null}
                                        fallbackCentre={locationCentre(li)}
                                        onLocationChange={({ latitude, longitude }) => handleLocationUpdate(li.id, latitude, longitude)}
                                    />
                                </div>
                            )}
                        </div>
                    )})}
                </div>
            </div>
            <input type="hidden" name="locationsJson" value={locationInstancesJson} />
        </div>
    );
};

export default LocationManager;
